import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable, of, timer } from 'rxjs';
import { catchError, delay, filter, first, map, mergeMap, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { BondModel, BondRequestModel, BondRequestUpdateModel, ShipmentModel } from '@data-layer/models/common';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { enumIsfShipmentType, enumIsfState, WizardProgressEnum } from '@data-layer/enums/isf';
import { enumBondRequestState, enumBondSubType, enumBondType, enumOcrStatus, enumPlatformType } from '@data-layer/enums/common';
import { enumPaymentProvider, enumPaymentState } from '@data-layer/enums/payments';
import {
  BondRequestRepository,
  CodeRepository,
  CompanyBondsRepository,
  DocumentsRepository,
  EntitiesRepository,
  InvoicesRepository,
  IsfBondsRepository,
  IsfMessagesRepository,
  IsfRepository,
  LineItemsRepository,
  ShipmentRepository,
  ShipmentsRepository,
} from '@data-layer/repositories';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { ManagementEntityEventModel } from '@data-layer/models/management';
import { CompaniesService } from '@services/companies.service';
import { IsfHelper } from '@helpers/isf.helper';
import { enumEntityByKey, enumEntityModelCode } from '@data-layer/enums/entities';
import { IsfEstimatedInfoModel, IsfModel, IsfShortModel } from '@data-layer/models/isf';
import { EntityModel } from '@data-layer/models/entities';
import { InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { enumGATagEventName } from '@enum/enumGATagEventName';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { CompanyModel } from '@data-layer/models/companies';
import { RequestLoaderService } from '@services/request-loader.service';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { MarineInsuranceAvailabilityService, MarineInsuranceFacade } from '@shared/marine-insurance-shared/services';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { LclIsfEsLinkModel, LCLShipmentModel } from '@data-layer/models/lcl';
import { DEFAULT_LONGER_POLLING_TIMEOUT, DEFAULT_MAX_POLLING_RETRY, USA_COUNTRY_CODE } from '@constants/constants';
import { flatten } from 'lodash-es';
import { IsfV1EntityHtsModel } from '@data-layer/models/isf-v1';
import { DocumentsService } from '@services/documents.service';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';

@Injectable({ providedIn: 'root' })
export class IsfStateService {
  public isf$: BehaviorSubject<IsfModel> = new BehaviorSubject<IsfModel>(null);
  public isfDocuments$: BehaviorSubject<DocumentModel[]> = new BehaviorSubject([]);
  public currentIsfDocOCRState$: BehaviorSubject<enumOcrStatus> = new BehaviorSubject(null);

  public _currentIsf: IsfModel;

  public get currentIsf(): IsfModel {
    return this._currentIsf;
  }

  public set currentIsf(isf: IsfModel) {
    this._currentIsf = isf;
    this.isf$.next(isf);
  }

  constructor(
    private googleAnalyticsService: GoogleAnalyticsService,
    private companyBondsRepository: CompanyBondsRepository,
    private isfRepository: IsfRepository,
    private documentsService: DocumentsService,
    private isfBondsRepository: IsfBondsRepository,
    private shipmentRepository: ShipmentsRepository,
    private codeRepository: CodeRepository,
    private entitiesRepository: EntitiesRepository,
    private companiesService: CompaniesService,
    private isfMessagesRepository: IsfMessagesRepository,
    private bondRequestRepository: BondRequestRepository,
    private invoicesRepository: InvoicesRepository,
    private lineItemsRepository: LineItemsRepository,
    private requestLoaderService: RequestLoaderService,
    private marineInsuranceAvailabilityService: MarineInsuranceAvailabilityService,
    private marineInsuranceFacade: MarineInsuranceFacade,
    private lclShipmentRepository: ShipmentRepository,
    private documentsRepository: DocumentsRepository,
  ) {}

  public createIsf(params?: Partial<IsfShortModel>) {
    const company = this.companiesService.getCompany();
    return this.isfRepository.create(company.id, { type: enumIsfShipmentType.FORMAL, ...params });
  }

  public resetIsf(isfData: Partial<IsfModel>, referenceNumber: string) {
    this.clearCurrentIsf();

    const company = this.companiesService.getCompany();
    if (!company) {
      return EMPTY;
    }

    let fullIsf: IsfModel;

    return this.getSummaryById(referenceNumber).pipe(
      switchMap((isf) => {
        fullIsf = isf;
        return this.isfRepository.update({ ...isf, ...isfData });
      }),
      switchMap((_) => this.updateShipment(isfData.shipment)),
      switchMap((_) => {
        return this.resetInvoice(isfData.shipment, fullIsf);
      }),
      tap((isf) => {
        this.updateCurrentIsf(isf);
      }),
      switchMap((isf) => {
        return this.updateIsf(isf);
      }),
      switchMap(() => this.loadIsf(referenceNumber)),
    );
  }

  public initBond(defaultBondReferenceNumber: string, companyId: number, isfId: number) {
    return defaultBondReferenceNumber
      ? this.companyBondsRepository.get(companyId, true).pipe(
          switchMap((bonds) => {
            const bondForIsf: BondModel = bonds.find(
              (bond) => bond.referenceNumber === defaultBondReferenceNumber && bond.type === enumBondType.CONTINUOUS,
            );
            return bondForIsf ? this.updateBond(bondForIsf, isfId) : of(null);
          }),
        )
      : of(null);
  }

  public updateIsf(isf: IsfModel, isShowLoader: boolean = true): Observable<IsfModel> {
    return this.isfRepository.update(isf, isShowLoader);
  }

  public updatePlatform(isfId: number, platformType: enumPlatformType): Observable<IsfModel> {
    return this.isfRepository.updatePlatform(isfId, platformType);
  }

  public pushManufacturer(manufacturerId: number) {
    return this.invoicesRepository.create(this.currentIsf.shipment.id, { manufacturerId });
  }

  public updateManufacturer(invoice: InvoiceModel, manufacturerId: number) {
    return this.invoicesRepository.update(invoice.id, { ...invoice, manufacturerId });
  }

  public deleteManufacturer(invoiceId: number) {
    return this.invoicesRepository.delete(invoiceId);
  }

  public unpushManufacturer(manufacturerId: number) {
    this.currentIsf.shipment.invoices = [
      ...this.currentIsf.shipment.invoices.filter((e: InvoiceModel) => e.manufacturerId !== manufacturerId),
    ];
  }

  public getSummaryById(referenceNumber: string): Observable<IsfModel> {
    return this.isfRepository.getSummary(referenceNumber);
  }

  public getIsfEntityById(keyName: enumEntityByKey): EntityModel {
    return !!this.currentIsf && IsfHelper.getIsfShipmentEntityById(this.currentIsf, keyName);
  }

  public getIsfManufacturerEntityList(): EntityModel[] {
    return IsfHelper.getIsfManufacturerEntityList(this.currentIsf) || [];
  }

  public getIsfInvoicesList(): InvoiceModel[] {
    return IsfHelper.getIsfInvoicesList(this.currentIsf);
  }

  public getIsfEntityByCode(code: enumEntityModelCode): EntityModel {
    return IsfHelper.getIsfShipmentEntityByCode(this.currentIsf, code);
  }

  public createEntity(data: Partial<EntityModel>): Observable<EntityModel> {
    if (!this.companiesService.getCompany()) {
      return of(null);
    }

    return this.entitiesRepository.createEntity(this.companiesService.getCompany().id, data);
  }

  public updateEntity(entity: EntityModel, isOnlyUsa?: boolean): Observable<EntityModel> {
    if (!this.companiesService.getCompany() || (isOnlyUsa && entity?.address?.country?.code !== USA_COUNTRY_CODE)) {
      return of(null);
    }

    if (!entity?.id) {
      return this.entitiesRepository.createEntity(this.companiesService.getCompany().id, entity).pipe(
        tap(() => {
          this.googleAnalyticsService.pushEventToDataLayer(
            enumGATagEventName.Isf,
            enumGATagEventCategory.Isf,
            enumGATagEventAction.IsfCreate,
            `ISF WIZARD ${this.currentIsf?.referenceNumber}`,
            1,
          );
        }),
      );
    }

    return this.entitiesRepository.updateEntity(entity.id, entity);
  }

  public deleteIsfEntity(id: number) {
    return this.entitiesRepository.deleteEntity(id);
  }

  public createLineItem(item: Partial<LineModel>, invoiceId: number) {
    return this.lineItemsRepository.create(invoiceId, item);
  }

  public updateLineItem(item: Partial<LineModel>): Observable<LineModel> {
    return this.lineItemsRepository.update(item.id, item);
  }

  public deleteLineItem(itemId: number, invoiceId?: number): Observable<never> {
    return this.lineItemsRepository.delete(itemId).pipe(
      tap(() => {
        if (!!invoiceId) {
          const manufacturerInvoices = (this.currentIsf?.shipment?.invoices || []).find((item) => item.id === invoiceId);
          const manufacturerLines = (manufacturerInvoices?.lines || []).filter((item) => item.id !== itemId);
          manufacturerInvoices.lines = manufacturerLines;
        }
      }),
    );
  }

  public clearData() {
    this.currentIsf = null;
  }

  public getShipment(): ShipmentModel {
    return IsfHelper.getFullShipment(this.currentIsf);
  }

  public updateShipment(shipment: ShipmentModel, isShowLoader: boolean = true): Observable<ShipmentModel> {
    return this.shipmentRepository.updateFull(shipment.id, shipment, isShowLoader);
  }

  public getBond(): BondModel {
    return IsfHelper.getBond(this.currentIsf);
  }

  public getBondRequest(): BondRequestModel {
    return IsfHelper.getBondRequest(this.currentIsf);
  }

  public getEstimatedInformation(): IsfEstimatedInfoModel {
    return IsfHelper.getFullEstimatedInformation(this.currentIsf);
  }

  public pushBond(bondInfo: BondModel): void {
    this.currentIsf.bondInfo = bondInfo;
  }

  public updateBond(bond: BondModel, isfId?: number): Observable<BondModel> {
    return this.isfBondsRepository
      .updateIsfBond(isfId ? isfId : this.currentIsf.id, bond)
      .pipe(switchMap((data) => this.deleteBondRequest().pipe(map(() => data))))
      .pipe(
        map((data) => {
          this.pushBond(data);
          return data;
        }),
      );
  }

  public singleBondRequest(portCode: string): Observable<any> {
    return this.isfBondsRepository
      .updateIsfBond(this.currentIsf.id, {
        suretyCode: null,
        referenceNumber: null,
        type: null,
        bondAmount: null,
      })
      .pipe(
        switchMap(() => {
          return this.createOrUpdateBondRequest({
            bondType: enumBondType.SINGLE_TRANSACTION,
            portCode,
            bondSubType: enumBondSubType.SINGLE_ISF,
            isfId: this.currentIsf.id,
          });
        }),
        switchMap((value) =>
          (this.currentIsf.bondRequest && this.currentIsf.bondRequest.id === value.id
            ? of(null)
            : this.isfBondsRepository.connectBondRequest(this.currentIsf.id, value.id)
          ).pipe(map(() => value)),
        ),
        tap((value) => {
          this.currentIsf.bondRequest = value;
        }),
      );
  }

  public deleteBondRequest(isfId?: number): Observable<any> {
    const bondRequest = this.currentIsf.bondRequest;

    if (bondRequest && bondRequest.id) {
      return this.isfBondsRepository.disconnectBondRequest(isfId ? isfId : this.currentIsf.id).pipe(
        switchMap(() => {
          this.currentIsf.bondRequest = null;
          if (bondRequest.state === enumBondRequestState.CREATED) {
            return this.bondRequestRepository.delete(bondRequest.id);
          }

          return of(null);
        }),
      );
    }
    return of(null);
  }

  public loadIsf(referenceNumber?: string): Observable<IsfModel> {
    if (!referenceNumber) {
      referenceNumber = this.isf$.getValue().referenceNumber;
    }
    return this.isfRepository.getSummary(referenceNumber);
  }

  public updateCurrentIsf(data: Partial<IsfModel>) {
    this.updateNextIsf((isf) => ({ ...isf, ...data }));
  }

  public clearCurrentIsf() {
    this.isf$.complete();
    this.isfDocuments$.complete();
    this.isf$ = new BehaviorSubject({} as IsfModel);
    this.isfDocuments$ = new BehaviorSubject([]);
  }

  public sendIsf(id: number): Observable<IsfModel> {
    return this.isfRepository.send(id);
  }

  public getCBPMessages(id: number) {
    return this.isfMessagesRepository.getCBPMessages(id);
  }

  public getCbpRawMessages(id: number, isShowLoader: boolean = true): Observable<ManagementEntityEventModel[]> {
    return this.isfMessagesRepository.getCbpRawMessages(id, isShowLoader);
  }

  public getQuantityUnits(query: string = '') {
    return this.codeRepository.searchIsfUnits(query, false);
  }

  public searchByCompany(params: { type: enumEntityModelCode; name?: string; identifier?: string }): Observable<EntityModel[]> {
    if (!params.identifier) {
      delete params.identifier;
    }

    if (!params.name) {
      delete params.name;
    }

    const companyId: number = this.companiesService.getCompany() ? this.companiesService.getCompany().id : null;

    if (!companyId) {
      return of([]);
    }

    return this.entitiesRepository.searchByCompany(companyId, params, true).pipe(catchError(() => of([])));
  }

  public isCanEdit(state: enumIsfState, paymentState: enumPaymentState, userPaymentType: enumCompanyPaymentType) {
    return IsfHelper.isCanEdit(state, paymentState, userPaymentType);
  }

  public updateWizardStep(step: WizardProgressEnum): Observable<IsfModel> {
    return this.isfRepository.updateWizardStep(this.currentIsf.id, step);
  }

  public initImporterFromCompany(id: number = null, entityCode: enumEntityModelCode = enumEntityModelCode.IMPORTER): EntityModel {
    const company = this.companiesService.getCompany();
    const { address, dateOfBirth, identifier, identifierQualifier } = company;
    const name = company.businessName || `${company.firstName} ${company.lastName}`;

    return new EntityModel({
      id,
      address: { ...address, id: 0 },
      addressId: null,
      entityCode,
      dateOfBirth,
      name,
      entityIdentifier: identifier,
      entityIdentifierQualifier: identifierQualifier,
      // #13121: null contact so ISF submit doesn't overwrite the company's IOR contact on Shell
      phone: null,
      email: null,
    });
  }

  public createEmptyISF(isfShipmentData: Partial<ShipmentModel>, shipmentId: number, lclData: LclIsfEsLinkModel): Observable<IsfModel> {
    const company: CompanyModel = this.companiesService.getCompany();
    const defaultImporterData = !!isfShipmentData?.importer ? isfShipmentData?.importer : isfShipmentData?.consignee;
    return this.updateEntity({ ...defaultImporterData, id: null, entityCode: enumEntityModelCode.IMPORTER }, true).pipe(
      mergeMap((importer: EntityModel) =>
        combineLatest([of(importer), this.updateEntity({ ...isfShipmentData.seller, id: null, entityCode: enumEntityModelCode.SELLER })]),
      ),
      mergeMap(([importer, seller]: [EntityModel, EntityModel]) =>
        combineLatest([
          of(importer),
          of(seller),
          this.updateEntity({ ...isfShipmentData.consignee, id: null, entityCode: enumEntityModelCode.CONSIGNEE }, true),
        ]),
      ),
      mergeMap(([importer, seller, consignee]: [EntityModel, EntityModel, EntityModel]) =>
        combineLatest([of(seller), of(consignee), this.createIsf({ importerId: importer?.id || null })]),
      ),
      mergeMap(([seller, consignee, isf]: [EntityModel, EntityModel, IsfModel]) =>
        combineLatest([
          of(seller),
          of(consignee),
          of(isf),
          this.lclShipmentRepository.setISFData(shipmentId, { ...lclData, referenceNumber: isf.referenceNumber }),
        ]),
      ),
      mergeMap(([seller, consignee, isf, _]: [EntityModel, EntityModel, IsfModel, LCLShipmentModel]) =>
        combineLatest([
          of(isf),
          this.updateShipment({ ...isf.shipment, ...isfShipmentData, sellerId: seller?.id || null, consigneeId: consignee?.id || null }),
        ]),
      ),
      switchMap(([isf, shipment]) => {
        if (this.isHasIsfOcr) {
          return this.createEmptyInvoice(shipment).pipe(switchMap(() => of(isf)));
        } else {
          return of(isf);
        }
      }),
      tap((isf: IsfModel) => this.initBond(company.defaultBondReferenceNumber, company.id, isf.id)),
      tap(() => this.requestLoaderService.hideAPILoader()),
    );
  }

  public getIsfState(id: number): Observable<enumIsfState> {
    return this.isfRepository.getState(id);
  }

  public getInsuranceAvailability(referenceNumber: string): Observable<{ insurance: MarineInsuranceModel; isAvailable: boolean }> {
    return this.marineInsuranceAvailabilityService.isShowCreateForIsf(referenceNumber);
  }

  public getInsuranceDocuments(id: number): Observable<DocumentModel> {
    return this.marineInsuranceFacade.getDocuments(id);
  }

  public getInsurancePendingBills(
    companyId: number,
    referenceNumber: string,
    paymentProvider: enumPaymentProvider,
    paymentCodeId: number,
    isCalculateFee: boolean,
  ): Observable<PaymentBillItemPendingModel> {
    return this.marineInsuranceFacade.getPendingBills(companyId, [referenceNumber], paymentProvider, paymentCodeId, isCalculateFee);
  }

  public createBasicData(isCreateEmptyInvoice: boolean = false): Observable<IsfModel> {
    if (!this.currentIsf) {
      return this.updateEntity(this.initImporterFromCompany()).pipe(
        switchMap((entity: EntityModel) => this.createIsf({ importerId: entity.id, platformType: enumPlatformType.DESKTOP })),
        tap((isf: IsfModel) => (this.currentIsf = isf)),
        switchMap((isf) => {
          const shipement = isf?.shipment;

          if (isCreateEmptyInvoice) {
            return this.createEmptyInvoice(shipement).pipe(switchMap(() => of(isf)));
          } else {
            return of(isf);
          }
        }),
        take(1),
      );
    } else {
      return of(this.currentIsf);
    }
  }

  public saveHTS(entityHts: any, invoices: InvoiceModel[], initialValue: IsfV1EntityHtsModel[]): Observable<IsfModel> {
    // update line item
    const addedLineItems = entityHts.filter((lineItem) => !lineItem.id || lineItem.id === 0);
    const updatedLineItems = entityHts.filter((lineItem) => lineItem.id);
    const exclude = (arr1, arr2) => arr1.filter((o1) => arr2.map((o2) => o2.id).indexOf(o1.id) === -1);
    const deletedLineItems = exclude(initialValue, entityHts);

    const lines = flatten(invoices.map((invoice) => invoice?.lines));
    const updateCalls = [];
    updatedLineItems.forEach((lineItem) => {
      updateCalls.push(
        this.updateLineItem({
          ...lines.find((line) => line.id === lineItem.id),
          countryOfOriginId: lineItem.countryOfOriginId,
          htsNumber: String(lineItem.number),
        }),
      );
    });

    const addedCalls = [];
    addedLineItems.forEach((lineItem) => {
      addedCalls.push(
        this.createLineItem(
          {
            ...lines.find((line) => line.id === lineItem.id),
            countryOfOriginId: lineItem.countryOfOriginId,
            htsNumber: String(lineItem.number),
          },
          lineItem.invoiceId,
        ),
      );
    });

    const deleteCalls = [];
    deletedLineItems.forEach((lineItem) => {
      deleteCalls.push(this.deleteLineItem(lineItem.id));
    });

    const allCalls = [...updateCalls, ...addedCalls, ...deleteCalls];

    if (allCalls.length === 0) {
      return of(this.currentIsf);
    } else {
      return forkJoin(allCalls).pipe(
        switchMap(() => this.getSummaryById(this.currentIsf?.referenceNumber)),
        tap((isf: IsfModel) => (this.currentIsf = isf)),
      );
    }
  }

  public getFormHTSValue(fgValue: any): IsfV1EntityHtsModel[] {
    const entityHts /*: IsfV1EntityHtsModel[]*/ = [];

    fgValue.manufacturers.forEach((manufacturer) => {
      manufacturer.hts.forEach((e) => {
        if (!!e.number || !!e.countryOfOriginId) {
          entityHts.push({
            id: e.id,
            invoiceId: manufacturer.invoiceId,
            number: e.number || '',
            countryOfOrigin: e.countryOfOrigin,
            countryOfOriginId: e.countryOfOriginId,
          });
        }
      });
    });

    return entityHts;
  }

  public prepareIsfData(thisCtx: any): void {
    thisCtx.isf = { ...this.currentIsf };

    thisCtx.importer = !!this.currentIsf ? this.getIsfEntityById(enumEntityByKey.IMPORTER) : this.initImporterFromCompany();
    thisCtx.consignee = this.getIsfEntityById(enumEntityByKey.CONSIGNEE);
    thisCtx.buyer = this.getIsfEntityById(enumEntityByKey.BUYER);
    thisCtx.shipTo = this.getIsfEntityById(enumEntityByKey.SHIP_TO);

    thisCtx.isImporterEdited = thisCtx.isEditedEntity(thisCtx.importer);
    thisCtx.isConsigneeEdited = thisCtx.isEditedEntity(thisCtx.consignee);
    thisCtx.isBuyerEdited = thisCtx.isEditedEntity(thisCtx.buyer);
    thisCtx.isShipToEdited = thisCtx.isEditedEntity(thisCtx.shipTo);

    thisCtx.seller = this.getIsfEntityById(enumEntityByKey.SELLER);
    thisCtx.consolidator = this.getIsfEntityById(enumEntityByKey.CONSOLIDATOR);
    thisCtx.staffingLocation = this.getIsfEntityById(enumEntityByKey.STAFFING_LOCATION);

    thisCtx.isSellerEdited = thisCtx.isEditedEntity(thisCtx.seller);
    thisCtx.isConsolidatorEdited = thisCtx.isEditedEntity(thisCtx.consolidator);
    thisCtx.isStaffingLocationEdited = thisCtx.isEditedEntity(thisCtx.staffingLocation);

    thisCtx.manufacturers = this.getIsfManufacturerEntityList();
    thisCtx.invoices = this.getIsfInvoicesList();
  }

  public uploadDocument(document: DocumentCreateModel, file: File, hasPendingBills: boolean, companyId: number = null) {
    return this.uploadDocumentClear(document, file, companyId).pipe(
      switchMap((docId) =>
        this.updateDocuments(false).pipe(
          map(() => {
            return docId;
          }),
        ),
      ),
      tap(() => {
        if (!hasPendingBills && this.isf$.getValue().state === enumIsfState.PROVIDER_SEND_PENDING) {
          this.afterUploadDocument();
        }
      }),
    );
  }

  public updateDocumentsState(hasPendingBills: boolean) {
    return this.updateDocuments(false).pipe(
      tap(() => {
        if (!hasPendingBills && this.isf$.getValue().state === enumIsfState.PROVIDER_SEND_PENDING) {
          this.afterUploadDocument();
        }
      }),
    );
  }

  public uploadDocumentClear(document: DocumentCreateModel, file: File, companyId: number = null): Observable<number> {
    return this.documentsService.uploadDocument(document, file, companyId);
  }

  public updateDocuments(isShowLoader: boolean) {
    return this.getOneIsf().pipe(
      switchMap((isf) => this.documentsService.getIsfDocuments(isf.id, isShowLoader)),
      catchError(() => []),
      tap((documents) => {
        this.isfDocuments$.next(documents);
      }),
    );
  }

  public getIsfOcrStatus(isfId: number, isShowLoader: boolean = false): Observable<enumOcrStatus> {
    return this.isfRepository.getOcrStatus(isfId, isShowLoader);
  }

  public getIsfDocOcrStatus(isfId: number, documentId: number, isShowLoader: boolean = false): Observable<enumOcrStatus> {
    return this.documentsRepository.getIsfDocuments(isfId, isShowLoader).pipe(
      map((docs) => {
        return docs.find((doc) => doc.id === documentId).ocrStatus;
      }),
    );
  }

  public processIsfOCR(isfId: number, docId: number, isClearStatusOnFinish: boolean = false): Observable<enumOcrStatus> {
    let retriesCounter: number = 0;
    let currentStatus: enumOcrStatus;

    return this.isfRepository.ocrProcessing(isfId).pipe(
      delay(DEFAULT_LONGER_POLLING_TIMEOUT), //timeout to allow user enough time to see status of ui change
      tap(() => this.currentIsfDocOCRState$.next(enumOcrStatus.PROCESSING)),
      switchMap(() => {
        return timer(0, DEFAULT_LONGER_POLLING_TIMEOUT).pipe(
          takeWhile(() => {
            const exitingStatuses = [
              enumOcrStatus.CANCELED,
              enumOcrStatus.EXTRACTION_FAILED,
              enumOcrStatus.PARTIALLY_PROCESSED,
              enumOcrStatus.PROCESSED,
              enumOcrStatus.CLASSIFYING_LINE_ITEMS,
            ];
            return retriesCounter < DEFAULT_MAX_POLLING_RETRY && !exitingStatuses.includes(currentStatus);
          }),
          switchMap(() =>
            this.getIsfDocOcrStatus(isfId, docId, false).pipe(
              catchError((err) => {
                return of(enumOcrStatus.PROCESSING);
              }),
            ),
          ),
          catchError((err) => {
            return of(enumOcrStatus.PROCESSING);
          }),
          tap((status: enumOcrStatus) => {
            currentStatus = status;
            if (status > enumOcrStatus.PROCESSING) {
              this.currentIsfDocOCRState$.next(status);
            }
          }),
          switchMap((status: enumOcrStatus) => {
            retriesCounter++;
            if (retriesCounter < DEFAULT_MAX_POLLING_RETRY && status !== enumOcrStatus.EXTRACTION_FAILED) {
              return of(enumOcrStatus.PROCESSING);
            } else {
              return of(status);
            }
          }),
          delay(DEFAULT_LONGER_POLLING_TIMEOUT), //timeout to allow user enough time to see status of ui change
          filter((status: enumOcrStatus) => {
            const statusesToSkip = [enumOcrStatus.CONVERTING_FILE_TO_IMAGE, enumOcrStatus.EXTRACTING_DATA];

            if (retriesCounter < DEFAULT_MAX_POLLING_RETRY) {
              statusesToSkip.push(enumOcrStatus.PROCESSING);
            }

            return !statusesToSkip.includes(status);
          }),
          first(),
          catchError((err) => of(currentStatus)),
        );
      }),
      tap(() => {
        if (isClearStatusOnFinish) {
          this.currentIsfDocOCRState$.next(null);
        }
      }),
    );
  }

  public processIsfDocOCR(
    isfId: number,
    data: { file: File; docId: number; docHash: string },
    isClearStatusOnFinish: boolean = false,
  ): Observable<enumOcrStatus> {
    const docId = data?.docId;

    let retriesCounter: number = 0;
    let currentStatus: enumOcrStatus;
    const exitingStatuses = [
      enumOcrStatus.CANCELED,
      enumOcrStatus.EXTRACTION_FAILED,
      enumOcrStatus.PARTIALLY_PROCESSED,
      enumOcrStatus.PROCESSED,
      enumOcrStatus.CLASSIFYING_LINE_ITEMS,
    ];

    return this.documentsRepository.ocrProcessing(docId).pipe(
      delay(DEFAULT_LONGER_POLLING_TIMEOUT), //timeout to allow user enough time to see status of ui change
      tap(() => this.currentIsfDocOCRState$.next(enumOcrStatus.PROCESSING)),
      switchMap(() => (!!data?.file ? this.uploadFile(data, true) : of(null))),
      switchMap(() => {
        return timer(0, DEFAULT_LONGER_POLLING_TIMEOUT).pipe(
          takeWhile(() => {
            return retriesCounter < DEFAULT_MAX_POLLING_RETRY && !exitingStatuses.includes(currentStatus);
          }),
          switchMap(() =>
            this.getIsfDocOcrStatus(isfId, docId, false).pipe(
              catchError((err) => {
                return of(enumOcrStatus.PROCESSING);
              }),
            ),
          ),
          catchError((err) => {
            return of(enumOcrStatus.PROCESSING);
          }),
          tap((status: enumOcrStatus) => {
            currentStatus = status;
            if (status > enumOcrStatus.PROCESSING) {
              this.currentIsfDocOCRState$.next(status);
            }
          }),
          switchMap((status: enumOcrStatus) => {
            retriesCounter++;
            if (retriesCounter < DEFAULT_MAX_POLLING_RETRY && !exitingStatuses.includes(currentStatus)) {
              return of(enumOcrStatus.PROCESSING);
            } else {
              return of(status);
            }
          }),
          delay(DEFAULT_LONGER_POLLING_TIMEOUT), //timeout to allow user enough time to see status of ui change
          filter((status: enumOcrStatus) => {
            const statusesToSkip = [enumOcrStatus.CONVERTING_FILE_TO_IMAGE, enumOcrStatus.EXTRACTING_DATA];

            if (retriesCounter < DEFAULT_MAX_POLLING_RETRY) {
              statusesToSkip.push(enumOcrStatus.PROCESSING);
            }

            return !statusesToSkip.includes(status);
          }),
          first(),
        );
      }),
      tap(() => {
        if (isClearStatusOnFinish) {
          this.currentIsfDocOCRState$.next(null);
        }
      }),
    );
  }

  private createEmptyInvoice(shipment: ShipmentModel) {
    let newInvoiceData: Partial<InvoiceModel> = {
      manufacturerId: null,
      manufacturer: null,
      invoiceNumber: null,
      currency: DEFAULT_CURRENCY_CODE,
      incoTermsCode: null,
      countryOfExportId: null,
      countryOfExport: null,
    };

    return this.createInvoice(shipment, newInvoiceData);
  }

  private createInvoice(shipment: ShipmentModel, newInvoiceData?: Partial<InvoiceModel>) {
    return this.invoicesRepository.create(shipment.id, newInvoiceData);
  }

  private resetInvoice(shipmentToUpdate: ShipmentModel, isf: IsfModel): Observable<IsfModel> {
    const invoices: InvoiceModel[] = shipmentToUpdate.invoices;

    if (!invoices?.length) {
      return of(isf);
    } else {
      return forkJoin(
        invoices.map((invoice) => {
          return this.cleanInvoice(invoice.id, invoice);
        }),
      ).pipe(switchMap((_) => of(isf)));
    }
  }

  private cleanInvoice(invoiceId: number, invoiceData: Partial<InvoiceModel>): Observable<InvoiceModel> {
    let isManufacturerDeleted = false;

    return this.invoicesRepository.update(invoiceId, invoiceData).pipe(
      switchMap((invoice: InvoiceModel) => {
        const isDeleteManufacturer =
          !!invoice && !!invoiceData.manufacturer && invoiceData.manufacturer?.entityCode === enumEntityModelCode.MANUFACTURER;

        if (isDeleteManufacturer) {
          return this.deleteManufacturer(invoiceId).pipe(
            map(() => {
              isManufacturerDeleted = true;
              return invoice;
            }),
          );
        } else {
          return of(invoice);
        }
      }),
      switchMap((invoice) => (isManufacturerDeleted ? of(invoice) : this.invoicesRepository.get(invoiceId))),
      switchMap((invoice: InvoiceModel) => {
        return invoice?.lines?.length && !isManufacturerDeleted
          ? forkJoin(invoice.lines.map((line: LineModel) => this.deleteLineItem(line.id))).pipe(map(() => invoice))
          : of(invoice);
      }),
    );
  }

  private createOrUpdateBondRequest(bondRequest: BondRequestUpdateModel): Observable<BondRequestModel> {
    if (
      this.currentIsf.bondRequest &&
      this.currentIsf.bondRequest.id &&
      this.currentIsf.bondRequest.state === enumBondRequestState.CREATED
    ) {
      return this.bondRequestRepository.update(this.currentIsf.bondRequest.id, bondRequest);
    }
    return this.bondRequestRepository.create(this.companiesService.getCompany().id, bondRequest);
  }

  private uploadFile(data: { file: File; docId: number; docHash: string }, isShowLoader: boolean): Observable<number> {
    const { file, docId, docHash } = data;

    return this.documentsRepository.uploadFile(docId, file, isShowLoader).pipe(
      map(() => docId),
      tap(() => this.documentsService.isUploadingDocIdsToUnshift$.next(docHash)),
      catchError(() => {
        return this.documentsRepository.delete(docId, isShowLoader);
      }),
    );
  }

  private afterUploadDocument() {
    const isf = this.isf$.getValue();
    this.isfRepository
      .getState(isf.id)
      .pipe(take(1))
      .subscribe((state) => {
        if (state !== enumIsfState.PROVIDER_SEND_PENDING) {
          this.afterUploadDocument();
        } else {
          this.updateNextIsf(() => ({
            state: state,
          }));
        }
      });
  }

  private updateNextIsf(updateFunc: (isf: IsfModel) => Partial<IsfModel>) {
    this.getOneIsf()
      .pipe(take(1))
      .subscribe((isf) => {
        const result = { ...isf, ...updateFunc(isf) };
        this.isf$.next(result);
      });
  }

  private getOneIsf() {
    return this.isf$.pipe(take(1));
  }

  private get isHasIsfOcr(): boolean {
    const isWithUserWithIsfOcrFlag = this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.isfOcr);

    return isWithUserWithIsfOcrFlag;
  }
}
